import { RadioButtonOption, SignatureType } from "../../../../common/enums";
import { FormState_PopulateErrorMessages } from "../../../../common/formStateUtils";
import { RiskIdentifications } from "../../../permits/conditions/RiskIdentifications";
import Errors from "../../../responses/Errors";
import { Signature_empty } from "../../../Signature";
import FormElement, { formElement } from "../../FormElement";
import AsbestosChecklistFormState from "./AsbestosChecklistFormState";
import BeefGasChecklistFormState from "./BeefGasChecklistFormState";
import ClosedSpaceChecklistFormState from "./ClosedSpaceChecklistFormState";
import DivingChecklistFormState from "./DivingChecklistFormState";
import ExcavationAndPolingChecklistFormState from "./ExcavationAndPolingChecklistFormState";
import { HeavyLiftingChecklistFormState } from "./HeavyLiftingChecklistFormState";
import HydrogenSulphideChecklistFormState from "./HydrogenSulphideChecklistFormState";
import RaftChecklistFormState from "./RaftChecklistFormState";
import ScaffoldingChecklistFormState from "./ScaffoldingChecklistFormState";
import ScaffoldingConstructionChecklistFormState from "./ScaffoldingConstructionChecklistFormState";
import ScaffoldingWorkChecklistFormState from "./ScaffoldingWorkChecklistFormState";
import WorkAtHeightChecklistFormState from "./WorkAtHeightChecklistFormState";

type RiskIdentificationsFormState = {
    isWorkAtHeightIncludedInPrint: FormElement<boolean>;
    workAtHeightChecklist: WorkAtHeightChecklistFormState;
    isScaffoldingIncludedInPrint: FormElement<boolean>;
    scaffoldingChecklist: ScaffoldingChecklistFormState;
    scaffoldingConstruction: FormElement<boolean>;
    scaffoldingConstructionChecklist: ScaffoldingConstructionChecklistFormState;
    scaffoldingWork: FormElement<boolean>;
    scaffoldingWorkChecklist: ScaffoldingWorkChecklistFormState;
    isClosedSpaceIncludedInPrint: FormElement<boolean>;
    closedSpaceChecklist: ClosedSpaceChecklistFormState;
    isAsbestosIncludedInPrint: FormElement<boolean>;
    asbetestosChecklist: AsbestosChecklistFormState;
    isHydrogenSulphideIncludedInPrint: FormElement<boolean>;
    hydrogenSulphideChecklist: HydrogenSulphideChecklistFormState;
    isBeefGasIncludedInPrint: FormElement<boolean>;
    beefGasChecklist: BeefGasChecklistFormState;
    isHeavyLiftingIncludedInPrint: FormElement<boolean>;
    heavyLiftingChecklist: HeavyLiftingChecklistFormState;
    isExcavationAndPolingIncludedInPrint: FormElement<boolean>;
    excavationAndPolingChecklist: ExcavationAndPolingChecklistFormState;
    isRaftIncludedInPrint: FormElement<boolean>;
    raftChecklist: RaftChecklistFormState;
    isDivingIncludedInPrint: FormElement<boolean>;
    divingChecklist: DivingChecklistFormState;
    interventionOnProcessEquipment: FormElement<boolean>;
};

export default RiskIdentificationsFormState;

export function RiskIdentificationsFormState_fromFormState(
    riskIdentifications: RiskIdentificationsFormState
): RiskIdentifications {
    const asbestosChecklist = riskIdentifications.asbetestosChecklist;
    const beefGasChecklist = riskIdentifications.beefGasChecklist;
    const closedSpaceChecklist = riskIdentifications.closedSpaceChecklist;
    const excavationAndPolingChecklist = riskIdentifications.excavationAndPolingChecklist;
    const heavyLiftingChecklist = riskIdentifications.heavyLiftingChecklist;
    const hydrogenSulphideChecklist = riskIdentifications.hydrogenSulphideChecklist;
    const scaffoldingChecklist = riskIdentifications.scaffoldingChecklist;
    const scaffoldingConstructionChecklist = riskIdentifications.scaffoldingConstructionChecklist;
    const scaffoldingWorkChecklist = riskIdentifications.scaffoldingWorkChecklist;
    const workAtHeightChecklist = riskIdentifications.workAtHeightChecklist;

    return {
        asbetestosChecklist: {
            areMasksFittedForRespPerson: asbestosChecklist.areMasksFittedForRespPerson.value,
            areOwnMachinesUsedForSanitation: asbestosChecklist.areOwnMachinesUsedForSanitation.value,
            externalPersonalIsContracted: asbestosChecklist.externalPersonalIsContracted.value,
            hasApprovalFromEnvironmentalAgency: asbestosChecklist.hasApprovalFromEnvironmentalAgency.value,
            hasCorrectDocuments: asbestosChecklist.hasCorrectDocuments.value,
            hasSignForAsbestos: asbestosChecklist.hasSignForAsbestos.value,
            isSanitisedFromAsbestosDust: asbestosChecklist.isSanitisedFromAsbestosDust.value,
            isTotalTimeForSanitationMoreThanOneHour: asbestosChecklist.isTotalTimeForSanitationMoreThanOneHour.value,
            isWholeMaskWithCompressedAirUsed: asbestosChecklist.isWholeMaskWithCompressedAirUsed.value,
            leaderAndPersonalHaveCorrectEducation: asbestosChecklist.leaderAndPersonalHaveCorrectEducation.value,
            masksAreStoredAccordingAccordingToRules: asbestosChecklist.masksAreStoredAccordingAccordingToRules.value,
            hasApprovalForThisProject: asbestosChecklist.hasApprovalForThisProject.value,
            checklistVersion: undefined
        },
        beefGasChecklist: {
            isContinousGasMeasurementsInPlace: beefGasChecklist.isContinousGasMeasurementsInPlace.value,
            isContinuousVentilationInPlace: beefGasChecklist.isContinuousVentilationInPlace.value,
            isFreshAirMaskForEveryone: beefGasChecklist.isFreshAirMaskForEveryone.value,
            isPortableGasMeasurementAlarmAvailableForEveryone:
                beefGasChecklist.isPortableGasMeasurementAlarmAvailableForEveryone.value,
            needsFreshAirDevice: beefGasChecklist.needsFreshAirDevice.value,
            checklistVersion: undefined,
            isContinousGasMeasurementsInPlaceForEntirityOfWork:
                beefGasChecklist.isContinousGasMeasurementsInPlaceForEntirityOfWork.value,
            isContinuousVentilationNeeded: beefGasChecklist.isContinuousVentilationNeeded.value,
            isFreshAirMaskNeededDuringWork: beefGasChecklist.isFreshAirMaskNeededDuringWork.value,
            isOxygenLevelGasMeterAvailable: beefGasChecklist.isOxygenLevelGasMeterAvailable.value,
            isWindDirectionConsideredDuringWork: beefGasChecklist.isWindDirectionConsideredDuringWork.value
        },
        closedSpaceChecklist: {
            checklistVersion: undefined,
            areAllTermsMet: closedSpaceChecklist.areAllTermsMet.value,
            areGasMeasurementsPerformed: closedSpaceChecklist.areGasMeasurementsPerformed.value,
            areGasMeasurementsPerformedInClosedSpace:
                closedSpaceChecklist.areGasMeasurementsPerformedInClosedSpace.value,
            areWarningSignsOnManholes: closedSpaceChecklist.areWarningSignsOnManholes.value,
            hasRescueHarness: closedSpaceChecklist.hasRescueHarness.value,
            isLightningSufficient: closedSpaceChecklist.isLightningSufficient.value,
            isManHoleGuardAvailable: closedSpaceChecklist.isManHoleGuardAvailable.value,
            isMechanicalVentilation: closedSpaceChecklist.isMechanicalVentilation.value,
            isPersonalAtEntryToClosedSpace: closedSpaceChecklist.isPersonalAtEntryToClosedSpace.value,
            isThereARescuePlan: closedSpaceChecklist.isThereARescuePlan.value,
            isThereEmergencyEquipment: closedSpaceChecklist.isThereEmergencyEquipment.value,
            willThereBeRescuePlan: closedSpaceChecklist.willThereBeRescuePlan.value,
            willYouHaveARescueHarness: closedSpaceChecklist.willYouHaveARescueHarness.value,
            willThereBeEmergencyEquipment: closedSpaceChecklist.willThereBeEmergencyEquipment.value,
            isTrainedManHoleGuardAvailable: closedSpaceChecklist.isTrainedManHoleGuardAvailable.value,
            isOtherPersonnelAtEntry: closedSpaceChecklist.isOtherPersonnelAtEntry.value,
            willGasMeasurementsBePerformedAndDocumented:
                closedSpaceChecklist.willGasMeasurementsBePerformedAndDocumented.value,
            areWarningSignsOnOpenings: closedSpaceChecklist.areWarningSignsOnOpenings.value
        },
        divingChecklist: {
            divingPermitSequenceIdentifier: riskIdentifications.divingChecklist.divingPermitSequenceIdentifier.value
        },
        excavationAndPolingChecklist: {
            checklistVersion: undefined,
            canSafleyDecendShaft: excavationAndPolingChecklist.canSafleyDecendShaft.value,
            howIsAreaBlockedOff: excavationAndPolingChecklist.howIsAreaBlockedOff.value,
            isCablesInTheGround: excavationAndPolingChecklist.isCablesInTheGround.value,
            isDepperThan120Centimeters: excavationAndPolingChecklist.isDepperThan120Centimeters.value,
            isGasPartOfTheTest: excavationAndPolingChecklist.isGasPartOfTheTest.value,
            isManHoleGuardAvailable: excavationAndPolingChecklist.isManHoleGuardAvailable.value,
            isMassesOilContanimated: excavationAndPolingChecklist.isMassesOilContanimated.value,
            isProductPipesOrTheLikeInTheGround: excavationAndPolingChecklist.isProductPipesOrTheLikeInTheGround.value,
            isReleaseDone: excavationAndPolingChecklist.isReleaseDone.value,
            isRiskOfRace: excavationAndPolingChecklist.isRiskOfRace.value,
            isSubmittedToCountyBoard: excavationAndPolingChecklist.isSubmittedToCountyBoard.value,
            isTestsDoneForLab: excavationAndPolingChecklist.isTestsDoneForLab.value,
            isSubmittedToCountyBoardForExcavation: excavationAndPolingChecklist.isSubmittedToCountyBoardForExcavation.value,
            isGasTestedInHoleBeforeEntry: excavationAndPolingChecklist.isGasTestedInHoleBeforeEntry.value,
            isTrainedExcavationGuardAvailable: excavationAndPolingChecklist.isTrainedExcavationGuardAvailable.value
        },
        heavyLiftingChecklist: {
            hasInfraDoneExposuresOnQuay: heavyLiftingChecklist.hasInfraDoneExposuresOnQuay.value,
            ifApprovedByInfraName: heavyLiftingChecklist.ifApprovedByInfraName.value,
            ifApprovedByInfraNumber: heavyLiftingChecklist.ifApprovedByInfraNumber.value,
            isApprovedByInfra: heavyLiftingChecklist.isApprovedByInfra.value,
            isControlLineAvailable: heavyLiftingChecklist.isControlLineAvailable.value,
            isCrainOperatorEducated: heavyLiftingChecklist.isCrainOperatorEducated.value,
            isEquipmentTested: heavyLiftingChecklist.isEquipmentTested.value,
            isGroundGotBearingCapcity: heavyLiftingChecklist.isGroundGotBearingCapcity.value,
            isInfraContacted: heavyLiftingChecklist.isInfraContacted.value,
            isLiftAreaShutOff: heavyLiftingChecklist.isLiftAreaShutOff.value,
            isLiftingHookOpen: heavyLiftingChecklist.isLiftingHookOpen.value,
            isLiftLineCorrectForTheWeight: heavyLiftingChecklist.isLiftLineCorrectForTheWeight.value,
            isLiftLineInGoodCondition: heavyLiftingChecklist.isLiftLineInGoodCondition.value,
            isLiftPlanInPlace: heavyLiftingChecklist.isLiftPlanInPlace.value,
            isRiskanalysDone: heavyLiftingChecklist.isRiskanalysDone.value,
            isSignalmanChoosen: heavyLiftingChecklist.isSignalmanChoosen.value,
            isSupportLegsCorrect: heavyLiftingChecklist.isSupportLegsCorrect.value,
            isVehicleAroundRorgatan: heavyLiftingChecklist.isVehicleAroundRorgatan.value,
            isWorkDoneBeneathLoad: heavyLiftingChecklist.isWorkDoneBeneathLoad.value,
            craneOperatorSignature: Signature_empty(SignatureType.CraneOperator),
            checklistVersion: undefined,
            isInfrastructureDepartmentContacted: heavyLiftingChecklist.isInfrastructureDepartmentContacted.value,
            isLiftAreaShutOff_Cones: heavyLiftingChecklist.isLiftAreaShutOff_Cones.value,
            isLiftAreaShutOff_NA: heavyLiftingChecklist.isLiftAreaShutOff_NA.value,
            isLiftAreaShutOff_Tape: heavyLiftingChecklist.isLiftAreaShutOff_Tape.value,
            isOpenLiftingHookUsed: heavyLiftingChecklist.isOpenLiftingHookUsed.value
        },
        hydrogenSulphideChecklist: {
            isContinousGasMeasurementsInPlace: hydrogenSulphideChecklist.isContinousGasMeasurementsInPlace.value,
            isContinuousVentilationInPlace: hydrogenSulphideChecklist.isContinuousVentilationInPlace.value,
            isEscapeMasksAvailable: hydrogenSulphideChecklist.isEscapeMasksAvailable.value,
            isPortalHydroGenSulphideAlarmAvailable:
                hydrogenSulphideChecklist.isPortalHydroGenSulphideAlarmAvailable.value,
            isWindDirectionThoughtOf: hydrogenSulphideChecklist.isWindDirectionThoughtOf.value,
            checklistVersion: undefined,
            isContinousGasMeasurementsInPlaceForEntirityOfWork:
                hydrogenSulphideChecklist.isContinousGasMeasurementsInPlaceForEntirityOfWork.value,
            isContinuousVentilationNeeded: hydrogenSulphideChecklist.isContinuousVentilationNeeded.value,
            isEscapeMasksAvailableForAllInvolved: hydrogenSulphideChecklist.isEscapeMasksAvailableForAllInvolved.value,
            isFreshAirMaskNeededDuringWork: hydrogenSulphideChecklist.isFreshAirMaskNeededDuringWork.value,
            isHydrogenSulphideGasMeterAvailable: hydrogenSulphideChecklist.isHydrogenSulphideGasMeterAvailable.value,
            isWindDirectionConsideredDuringWork: hydrogenSulphideChecklist.isWindDirectionConsideredDuringWork.value
        },
        interventionOnProcessEquipment: riskIdentifications.interventionOnProcessEquipment.value,
        isAsbestosIncludedInPrint: riskIdentifications.isAsbestosIncludedInPrint.value,
        isBeefGasIncludedInPrint: riskIdentifications.isBeefGasIncludedInPrint.value,
        isClosedSpaceIncludedInPrint: riskIdentifications.isClosedSpaceIncludedInPrint.value,
        isDivingIncludedInPrint: riskIdentifications.isDivingIncludedInPrint.value,
        isExcavationAndPolingIncludedInPrint: riskIdentifications.isExcavationAndPolingIncludedInPrint.value,
        isHeavyLiftingIncludedInPrint: riskIdentifications.isHeavyLiftingIncludedInPrint.value,
        isHydrogenSulphideIncludedInPrint: riskIdentifications.isHydrogenSulphideIncludedInPrint.value,
        isRaftIncludedInPrint: riskIdentifications.isRaftIncludedInPrint.value,
        isScaffoldingIncludedInPrint: riskIdentifications.isScaffoldingIncludedInPrint.value,
        isWorkAtHeightIncludedInPrint: riskIdentifications.isWorkAtHeightIncludedInPrint.value,
        raftChecklist: {
            underWaterWorkPermitSequenceIdentifier:
                riskIdentifications.raftChecklist.underWaterWorkPermitSequenceIdentifier.value
        },
        scaffoldingChecklist: {
            areEscapeRoutesCleared: scaffoldingChecklist.areEscapeRoutesCleared.value,
            doesScaffoldHaveDamage: scaffoldingChecklist.doesScaffoldHaveDamage.value,
            doesScaffoldHaveSufficientRailing: scaffoldingChecklist.doesScaffoldHaveSufficientRailing.value,
            ifPassageAffectedIsMarked: scaffoldingChecklist.ifPassageAffectedIsMarked.value,
            isConnectedProperly: scaffoldingChecklist.isConnectedProperly.value,
            isHarnessInspected: scaffoldingChecklist.isHarnessInspected.value,
            isHarnessNeeded: scaffoldingChecklist.isHarnessNeeded.value,
            isIceOnScaffold: scaffoldingChecklist.isIceOnScaffold.value,
            isLadderHigherThan4MetersAndBackProtection:
                scaffoldingChecklist.isLadderHigherThan4MetersAndBackProtection.value,
            isOilSpillOnScaffold: scaffoldingChecklist.isOilSpillOnScaffold.value,
            isPassageAffectedByScaffold: scaffoldingChecklist.isPassageAffectedByScaffold.value,
            isScaffoldApproved: scaffoldingChecklist.isScaffoldApproved.value,
            isThereRiskOfStepThrough: scaffoldingChecklist.isThereRiskOfStepThrough.value,
            noLoosePlanksOnScaffold: scaffoldingChecklist.noLoosePlanksOnScaffold.value,
            willScaffoldsBeBuiltInPumpingStation: scaffoldingChecklist.willScaffoldsBeBuiltInPumpingStation.value
        },
        scaffoldingConstruction: riskIdentifications.scaffoldingConstruction.value,
        scaffoldingConstructionChecklist: {
            willBeBuiltInPumpingStation: scaffoldingConstructionChecklist.willBeBuiltInPumpingStation.value,
            willBeBuiltOnQuay: scaffoldingConstructionChecklist.willBeBuiltOnQuay.value,
            isFallProtectionHarnessNeeded: scaffoldingConstructionChecklist.isFallProtectionHarnessNeeded.value,
            isHarnessInspectedWithinOneYear: scaffoldingConstructionChecklist.isHarnessInspectedWithinOneYear.value,
            isSecurelyConnectedToFallProtection:
                scaffoldingConstructionChecklist.isSecurelyConnectedToFallProtection.value,
            willHaveApprovedValidityCertificate:
                scaffoldingConstructionChecklist.willHaveApprovedValidityCertificate.value,
            willHaveSufficientlyHighGuardRails:
                scaffoldingConstructionChecklist.willHaveSufficientlyHighGuardRails.value,
            willToeboardsBeMounted: scaffoldingConstructionChecklist.willToeboardsBeMounted.value,
            willHaveLoosePlanks: scaffoldingConstructionChecklist.willHaveLoosePlanks.value,
            isRiskOfSteppingThroughAfterCompletion:
                scaffoldingConstructionChecklist.isRiskOfSteppingThroughAfterCompletion.value,
            isBackProtectionMountedIfLadderAbove4m:
                scaffoldingConstructionChecklist.isBackProtectionMountedIfLadderAbove4m.value,
            areEscapeRoutesCleared: scaffoldingConstructionChecklist.areEscapeRoutesCleared.value,
            hasScaffoldingDamage: scaffoldingConstructionChecklist.hasScaffoldingDamage.value,
            hasOilSpill: scaffoldingConstructionChecklist.hasOilSpill.value,
            hasIce: scaffoldingConstructionChecklist.hasIce.value,
            isPassageAffectedByScaffolding: scaffoldingConstructionChecklist.isPassageAffectedByScaffolding.value,
            isPassageMarkedAndCollisionProtectionInstalled:
                scaffoldingConstructionChecklist.isPassageMarkedAndCollisionProtectionInstalled.value
        },
        scaffoldingWork: riskIdentifications.scaffoldingWork.value,
        scaffoldingWorkChecklist: {
            hasApprovedValidityCertificate: scaffoldingWorkChecklist.hasApprovedValidityCertificate.value,
            hasSufficientlyHighGuardRails: scaffoldingWorkChecklist.hasSufficientlyHighGuardRails.value,
            hasToeboards: scaffoldingWorkChecklist.hasToeboards.value,
            hasLoosePlanks: scaffoldingWorkChecklist.hasLoosePlanks.value,
            isRiskOfSteppingThrough: scaffoldingWorkChecklist.isRiskOfSteppingThrough.value,
            isBackProtectionMountedIfLadderAbove4m:
                scaffoldingWorkChecklist.isBackProtectionMountedIfLadderAbove4m.value,
            areEscapeRoutesCleared: scaffoldingWorkChecklist.areEscapeRoutesCleared.value,
            hasScaffoldingDamage: scaffoldingWorkChecklist.hasScaffoldingDamage.value,
            hasOilSpill: scaffoldingWorkChecklist.hasOilSpill.value,
            hasIce: scaffoldingWorkChecklist.hasIce.value
        },
        workAtHeightChecklist: {
            checklistVersion: undefined,
            areEscapeRoutesCleared: workAtHeightChecklist.areEscapeRoutesCleared.value,
            haveCorrectFallProtection: workAtHeightChecklist.haveCorrectFallProtection.value,
            isAreaCordonedOff: workAtHeightChecklist.isAreaCordonedOff.value,
            isConnectedProperly: workAtHeightChecklist.isConnectedProperly.value,
            isHarnessInspected: workAtHeightChecklist.isHarnessInspected.value,
            isRiskOfDebris: workAtHeightChecklist.isRiskOfDebris.value,
            isRiskOfFallingObjects: workAtHeightChecklist.isRiskOfFallingObjects.value,
            needsProtectiveNet: workAtHeightChecklist.needsProtectiveNet.value,
            willBeLooseObjects: workAtHeightChecklist.willBeLooseObjects.value,
            willBeLooseObjectsComment: workAtHeightChecklist.willBeLooseObjectsComment.value,
            howIsAreaBelowCordonedOff: workAtHeightChecklist.howIsAreaBelowCordonedOff.value,
            isSecurelyConnectedToFallProtection: workAtHeightChecklist.isSecurelyConnectedToFallProtection.value,
            isHarnessInspectedWithinOneYear: workAtHeightChecklist.isHarnessInspectedWithinOneYear.value,
            howAreLooseObjectsSecured: workAtHeightChecklist.howAreLooseObjectsSecured.value
        }
    };
}

export function RiskIdentificationsFormState_default(): RiskIdentificationsFormState {
    return {
        asbetestosChecklist: {
            areMasksFittedForRespPerson: formElement(RadioButtonOption.None),
            areOwnMachinesUsedForSanitation: formElement(RadioButtonOption.None),
            externalPersonalIsContracted: formElement(RadioButtonOption.None),
            hasApprovalFromEnvironmentalAgency: formElement(RadioButtonOption.None),
            hasCorrectDocuments: formElement(RadioButtonOption.None),
            hasSignForAsbestos: formElement(RadioButtonOption.None),
            isSanitisedFromAsbestosDust: formElement(RadioButtonOption.None),
            isTotalTimeForSanitationMoreThanOneHour: formElement(RadioButtonOption.None),
            isWholeMaskWithCompressedAirUsed: formElement(RadioButtonOption.None),
            leaderAndPersonalHaveCorrectEducation: formElement(RadioButtonOption.None),
            masksAreStoredAccordingAccordingToRules: formElement(RadioButtonOption.None),
            hasApprovalForThisProject: formElement(RadioButtonOption.None)
        },
        beefGasChecklist: {
            isContinousGasMeasurementsInPlace: formElement(RadioButtonOption.None),
            isContinuousVentilationInPlace: formElement(RadioButtonOption.None),
            isFreshAirMaskForEveryone: formElement(RadioButtonOption.None),
            isPortableGasMeasurementAlarmAvailableForEveryone: formElement(RadioButtonOption.None),
            needsFreshAirDevice: formElement(RadioButtonOption.None),
            isContinousGasMeasurementsInPlaceForEntirityOfWork: formElement(RadioButtonOption.None),
            isFreshAirMaskNeededDuringWork: formElement(RadioButtonOption.None),
            isContinuousVentilationNeeded: formElement(RadioButtonOption.None),
            isOxygenLevelGasMeterAvailable: formElement(RadioButtonOption.None),
            isWindDirectionConsideredDuringWork: formElement(RadioButtonOption.None)
        },
        closedSpaceChecklist: {
            areAllTermsMet: formElement(RadioButtonOption.None),
            areGasMeasurementsPerformed: formElement(RadioButtonOption.None),
            areGasMeasurementsPerformedInClosedSpace: formElement(RadioButtonOption.None),
            areWarningSignsOnManholes: formElement(RadioButtonOption.None),
            hasRescueHarness: formElement(RadioButtonOption.None),
            isLightningSufficient: formElement(RadioButtonOption.None),
            isManHoleGuardAvailable: formElement(RadioButtonOption.None),
            isMechanicalVentilation: formElement(RadioButtonOption.None),
            isPersonalAtEntryToClosedSpace: formElement(RadioButtonOption.None),
            isThereARescuePlan: formElement(RadioButtonOption.None),
            isThereEmergencyEquipment: formElement(RadioButtonOption.None),
            areWarningSignsOnOpenings: formElement(RadioButtonOption.None),
            isOtherPersonnelAtEntry: formElement(RadioButtonOption.None),
            isTrainedManHoleGuardAvailable: formElement(RadioButtonOption.None),
            willGasMeasurementsBePerformedAndDocumented: formElement(RadioButtonOption.None),
            willThereBeEmergencyEquipment: formElement(RadioButtonOption.None),
            willThereBeRescuePlan: formElement(RadioButtonOption.None),
            willYouHaveARescueHarness: formElement(RadioButtonOption.None)
        },
        divingChecklist: {
            divingPermitSequenceIdentifier: formElement("")
        },
        excavationAndPolingChecklist: {
            canSafleyDecendShaft: formElement(RadioButtonOption.None),
            howIsAreaBlockedOff: formElement(RadioButtonOption.None),
            isCablesInTheGround: formElement(RadioButtonOption.None),
            isDepperThan120Centimeters: formElement(RadioButtonOption.None),
            isGasPartOfTheTest: formElement(RadioButtonOption.None),
            isManHoleGuardAvailable: formElement(RadioButtonOption.None),
            isMassesOilContanimated: formElement(RadioButtonOption.None),
            isProductPipesOrTheLikeInTheGround: formElement(RadioButtonOption.None),
            isReleaseDone: formElement(RadioButtonOption.None),
            isRiskOfRace: formElement(RadioButtonOption.None),
            isSubmittedToCountyBoard: formElement(RadioButtonOption.None),
            isTestsDoneForLab: formElement(RadioButtonOption.None),
            isSubmittedToCountyBoardForExcavation: formElement(RadioButtonOption.None),
            isGasTestedInHoleBeforeEntry: formElement(RadioButtonOption.None),
            isTrainedExcavationGuardAvailable: formElement(RadioButtonOption.None)
        },
        heavyLiftingChecklist: {
            hasInfraDoneExposuresOnQuay: formElement(RadioButtonOption.None),
            ifApprovedByInfraName: formElement(""),
            ifApprovedByInfraNumber: formElement(""),
            isApprovedByInfra: formElement(RadioButtonOption.None),
            isControlLineAvailable: formElement(RadioButtonOption.None),
            isCrainOperatorEducated: formElement(RadioButtonOption.None),
            isEquipmentTested: formElement(RadioButtonOption.None),
            isGroundGotBearingCapcity: formElement(RadioButtonOption.None),
            isInfraContacted: formElement(RadioButtonOption.None),
            isLiftAreaShutOff: formElement(RadioButtonOption.None),
            isLiftingHookOpen: formElement(RadioButtonOption.None),
            isLiftLineCorrectForTheWeight: formElement(RadioButtonOption.None),
            isLiftLineInGoodCondition: formElement(RadioButtonOption.None),
            isLiftPlanInPlace: formElement(RadioButtonOption.None),
            isRiskanalysDone: formElement(RadioButtonOption.None),
            isSignalmanChoosen: formElement(RadioButtonOption.None),
            isSupportLegsCorrect: formElement(RadioButtonOption.None),
            isVehicleAroundRorgatan: formElement(RadioButtonOption.None),
            isWorkDoneBeneathLoad: formElement(RadioButtonOption.None),
            isInfrastructureDepartmentContacted: formElement(RadioButtonOption.None),
            isLiftAreaShutOff_Cones: formElement(false),
            isLiftAreaShutOff_NA: formElement(false),
            isLiftAreaShutOff_Tape: formElement(false),
            isOpenLiftingHookUsed: formElement(RadioButtonOption.None)
        },
        hydrogenSulphideChecklist: {
            isContinousGasMeasurementsInPlace: formElement(RadioButtonOption.None),
            isContinuousVentilationInPlace: formElement(RadioButtonOption.None),
            isEscapeMasksAvailable: formElement(RadioButtonOption.None),
            isPortalHydroGenSulphideAlarmAvailable: formElement(RadioButtonOption.None),
            isWindDirectionThoughtOf: formElement(RadioButtonOption.None),
            isContinousGasMeasurementsInPlaceForEntirityOfWork: formElement(RadioButtonOption.None),
            isContinuousVentilationNeeded: formElement(RadioButtonOption.None),
            isEscapeMasksAvailableForAllInvolved: formElement(RadioButtonOption.None),
            isFreshAirMaskNeededDuringWork: formElement(RadioButtonOption.None),
            isHydrogenSulphideGasMeterAvailable: formElement(RadioButtonOption.None),
            isWindDirectionConsideredDuringWork: formElement(RadioButtonOption.None)
        },
        interventionOnProcessEquipment: formElement(false),
        isAsbestosIncludedInPrint: formElement(false),
        isBeefGasIncludedInPrint: formElement(false),
        isClosedSpaceIncludedInPrint: formElement(false),
        isDivingIncludedInPrint: formElement(false),
        isExcavationAndPolingIncludedInPrint: formElement(false),
        isHeavyLiftingIncludedInPrint: formElement(false),
        isHydrogenSulphideIncludedInPrint: formElement(false),
        isRaftIncludedInPrint: formElement(false),
        isScaffoldingIncludedInPrint: formElement(false),
        isWorkAtHeightIncludedInPrint: formElement(false),
        raftChecklist: {
            underWaterWorkPermitSequenceIdentifier: formElement("")
        },
        scaffoldingChecklist: {
            areEscapeRoutesCleared: formElement(RadioButtonOption.None),
            doesScaffoldHaveDamage: formElement(RadioButtonOption.None),
            doesScaffoldHaveSufficientRailing: formElement(RadioButtonOption.None),
            ifPassageAffectedIsMarked: formElement(RadioButtonOption.None),
            isConnectedProperly: formElement(RadioButtonOption.None),
            isHarnessInspected: formElement(RadioButtonOption.None),
            isHarnessNeeded: formElement(RadioButtonOption.None),
            isIceOnScaffold: formElement(RadioButtonOption.None),
            isLadderHigherThan4MetersAndBackProtection: formElement(RadioButtonOption.None),
            isOilSpillOnScaffold: formElement(RadioButtonOption.None),
            isPassageAffectedByScaffold: formElement(RadioButtonOption.None),
            isScaffoldApproved: formElement(RadioButtonOption.None),
            isThereRiskOfStepThrough: formElement(RadioButtonOption.None),
            noLoosePlanksOnScaffold: formElement(RadioButtonOption.None),
            willScaffoldsBeBuiltInPumpingStation: formElement(RadioButtonOption.None)
        },
        scaffoldingConstruction: formElement(false),
        scaffoldingConstructionChecklist: {
            willBeBuiltInPumpingStation: formElement(RadioButtonOption.None),
            willBeBuiltOnQuay: formElement(RadioButtonOption.None),
            isFallProtectionHarnessNeeded: formElement(RadioButtonOption.None),
            isHarnessInspectedWithinOneYear: formElement(RadioButtonOption.None),
            isSecurelyConnectedToFallProtection: formElement(RadioButtonOption.None),
            willHaveApprovedValidityCertificate: formElement(RadioButtonOption.None),
            willHaveSufficientlyHighGuardRails: formElement(RadioButtonOption.None),
            willToeboardsBeMounted: formElement(RadioButtonOption.None),
            willHaveLoosePlanks: formElement(RadioButtonOption.None),
            isRiskOfSteppingThroughAfterCompletion: formElement(RadioButtonOption.None),
            isBackProtectionMountedIfLadderAbove4m: formElement(RadioButtonOption.None),
            areEscapeRoutesCleared: formElement(RadioButtonOption.None),
            hasScaffoldingDamage: formElement(RadioButtonOption.None),
            hasOilSpill: formElement(RadioButtonOption.None),
            hasIce: formElement(RadioButtonOption.None),
            isPassageAffectedByScaffolding: formElement(RadioButtonOption.None),
            isPassageMarkedAndCollisionProtectionInstalled: formElement(RadioButtonOption.None)
        },
        scaffoldingWork: formElement(false),
        scaffoldingWorkChecklist: {
            areEscapeRoutesCleared: formElement(RadioButtonOption.None),
            hasApprovedValidityCertificate: formElement(RadioButtonOption.None),
            hasSufficientlyHighGuardRails: formElement(RadioButtonOption.None),
            hasToeboards: formElement(RadioButtonOption.None),
            hasIce: formElement(RadioButtonOption.None),
            hasLoosePlanks: formElement(RadioButtonOption.None),
            hasOilSpill: formElement(RadioButtonOption.None),
            hasScaffoldingDamage: formElement(RadioButtonOption.None),
            isBackProtectionMountedIfLadderAbove4m: formElement(RadioButtonOption.None),
            isRiskOfSteppingThrough: formElement(RadioButtonOption.None)
        },
        workAtHeightChecklist: {
            areEscapeRoutesCleared: formElement(RadioButtonOption.None),
            haveCorrectFallProtection: formElement(RadioButtonOption.None),
            isAreaCordonedOff: formElement(RadioButtonOption.None),
            isConnectedProperly: formElement(RadioButtonOption.None),
            isHarnessInspected: formElement(RadioButtonOption.None),
            isRiskOfDebris: formElement(RadioButtonOption.None),
            isRiskOfFallingObjects: formElement(RadioButtonOption.None),
            needsProtectiveNet: formElement(RadioButtonOption.None),
            willBeLooseObjects: formElement(RadioButtonOption.None),
            willBeLooseObjectsComment: formElement(""),
            howIsAreaBelowCordonedOff: formElement(""),
            isSecurelyConnectedToFallProtection: formElement(RadioButtonOption.None),
            isHarnessInspectedWithinOneYear: formElement(RadioButtonOption.None),
            howAreLooseObjectsSecured: formElement("")
        }
    };
}

export function RiskIdentificationsFormState_populateErrorMessages(
    formState: RiskIdentificationsFormState,
    errors: Errors
): RiskIdentificationsFormState {
    const errorKeys = Object.keys(errors);

    const raftErrors = errorKeys
        .filter((k) => k.startsWith("RaftChecklist."))
        .reduce((acc, key) => {
            const newKey = key.replace("RaftChecklist.", "") as keyof RaftChecklistFormState;
            acc[newKey] = errors[key];
            return acc;
        }, {} as Errors);

    const divingErrors = errorKeys
        .filter((k) => k.startsWith("DivingChecklist."))
        .reduce((acc, key) => {
            const newKey = key.replace("DivingChecklist.", "") as keyof DivingChecklistFormState;
            acc[newKey] = errors[key];
            return acc;
        }, {} as Errors);

    const updatedFormState = FormState_PopulateErrorMessages(formState, errors);
    updatedFormState.raftChecklist = FormState_PopulateErrorMessages(formState.raftChecklist, raftErrors);
    updatedFormState.divingChecklist = FormState_PopulateErrorMessages(formState.divingChecklist, divingErrors);

    return updatedFormState;
}
